import { ThemeEvents } from "@theme/events"

/**
 * Reflects the customer's product-mix selections (select[data-mix-cap], see
 * product-mix-products.liquid) in two completely independent places:
 *
 * 1. Small "material-selection-display" thumbnail (bottom-right overlay on the main gallery
 *    image) — one per category, shown additively whenever that category's selected product has
 *    its own featured image, exactly like the existing fabric/material picker's own thumbnail.
 *    Always the component product's own image, unconditionally — never touches the main gallery.
 * 2. The main gallery image — driven *entirely* by `product_mix_combo_image` entries
 *    (custom.product_mix_combo_images on the main product, rendered as JSON in
 *    [data-product-mix-combo-images]), never by a component's own featured image directly:
 *    - A two-sided entry (component_a AND component_b set) matches when both are currently
 *      selected across two categories.
 *    - A single-sided entry (only one of component_a/component_b set) matches when just that one
 *      product is selected, regardless of category or what else is selected.
 *    The first match replaces the base image (the gallery's first slide) in place — base shot and
 *    combo shot are the same product from the same angle, so appending the combo image would just
 *    park a near-duplicate at the end of the carousel. Several independent matches can be active at
 *    once (e.g. one pair + one unrelated single); every match beyond the first still gets its own
 *    appended slide. Either way the gallery jumps to whichever image the change produced.
 *
 * Both only ever reflect a REAL customer selection — never a display-only fallback — so the
 * required-selection/checkout gating (product-configuration-product.js) and cart/price logic stay
 * completely untouched by this module.
 *
 * The most recently activated combo-slide image is exposed as `data-configured-image` on the
 * closest .product__options-combined, which custom-product-form.js reads at submit time to
 * snapshot it onto the cart line item as the _configuredImage property.
 *
 * See docs/PRODUCT-MIX-GALLERY-SLIDE-PLAN.md for the full design/rationale.
 *
 * Deliberately its own small module (like price-aggregator.js) rather than growing
 * variant-custom-picker.js, which is already large and scoped to the real Shopify variant system.
 */

const COMBO_PREFIX = "product-mix-combo."

/**
 * @param {HTMLSelectElement} select
 * @returns {{ productId: string, image: string } | null}
 */
function getSelectedOption(select) {
  const option = select.options[select.selectedIndex]
  if (!option?.value || !option.dataset.mixProductId) return null

  return {
    productId: option.dataset.mixProductId,
    image: option.dataset.mixImage || "",
  }
}

/**
 * @param {HTMLElement} scope - .product__options-combined
 * @returns {Array<{ category: string, select: HTMLSelectElement }>}
 */
function getCategorySelects(scope) {
  return Array.from(scope.querySelectorAll("select[data-mix-cap]")).map((select) => ({
    category: (select.dataset.key || "").replace(/^product_mix\./, ""),
    select,
  }))
}

/**
 * @param {HTMLElement} scope
 * @returns {Array<{ a: string | null, b: string | null, image: string }>}
 */
function getComboEntries(scope) {
  const node = scope.querySelector("[data-product-mix-combo-images]")
  if (!node?.textContent) return []
  try {
    const parsed = JSON.parse(node.textContent)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/**
 * Resolves which `product_mix_combo_image` entries currently apply to the main gallery: two-sided
 * entries matched pairwise first (greedy), then single-sided entries for any category not already
 * absorbed into a pair. Entirely independent of each category's own featured image.
 * @param {Array<{ category: string, productId: string }>} selections - real selections only
 * @param {Array<{ a: string | null, b: string | null, image: string }>} comboEntries
 * @returns {Array<{ categories: string[], image: string, slideId: string }>}
 */
function resolveComboTargets(selections, comboEntries) {
  const pairEntries = comboEntries.filter((c) => c.a && c.b)
  const singleEntries = comboEntries.filter((c) => (c.a && !c.b) || (!c.a && c.b))
  const matches = []
  const handled = new Set()

  for (let i = 0; i < selections.length; i++) {
    if (handled.has(selections[i].category)) continue
    for (let j = i + 1; j < selections.length; j++) {
      if (handled.has(selections[j].category)) continue

      const idA = selections[i].productId
      const idB = selections[j].productId
      const combo = pairEntries.find((c) => (c.a === idA && c.b === idB) || (c.a === idB && c.b === idA))
      if (!combo) continue

      handled.add(selections[i].category)
      handled.add(selections[j].category)
      const slideId = COMBO_PREFIX + [selections[i].category, selections[j].category].sort().join(".")
      matches.push({ categories: [selections[i].category, selections[j].category], image: combo.image, slideId })
      break
    }
  }

  for (const selection of selections) {
    if (handled.has(selection.category)) continue
    const single = singleEntries.find((c) => c.a === selection.productId || c.b === selection.productId)
    if (!single) continue

    handled.add(selection.category)
    matches.push({ categories: [selection.category], image: single.image, slideId: COMBO_PREFIX + selection.category })
  }

  return matches
}

/**
 * Builds the inner markup of an injected combo slide/thumbnail — mirrors the shape of
 * snippets/product-media.liquid / snippets/slideshow-slide.liquid closely enough for the
 * slideshow/thumbnail components to treat it like any other slide, without needing a real
 * Shopify media object (we only ever have a plain image URL here).
 * @param {string} slideId
 * @param {string} image
 */
function buildSlide(slideId, image) {
  const slide = document.createElement("slideshow-slide")
  slide.setAttribute("ref", "slides[]")
  slide.setAttribute("slide-id", slideId)
  slide.setAttribute("aria-hidden", "true")
  slide.setAttribute("data-product-mix-slide", "")
  slide.style.setProperty("--product-media-fit", "cover")

  const media = document.createElement("div")
  media.className = "product-media"

  const img = document.createElement("img")
  img.className = "product-media__image"
  img.src = image
  img.loading = "lazy"

  media.appendChild(img)
  slide.appendChild(media)
  return slide
}

/**
 * @param {string} slideId
 * @param {string} image
 */
function buildThumbnail(slideId, image) {
  const button = document.createElement("button")
  button.type = "button"
  button.className = "slideshow-control button button-unstyled slideshow-controls__thumbnail"
  button.setAttribute("ref", "thumbnails[]")
  button.setAttribute("data-product-mix-slide", slideId)

  const img = document.createElement("img")
  img.src = image
  img.loading = "lazy"

  button.appendChild(img)
  return button
}

function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)))
}

/**
 * Inserts or updates the combo slide/thumbnail for a resolved target.
 * @param {Element} gallery
 * @param {string} slideId
 * @param {string} image
 */
function ensureSlide(gallery, slideId, image) {
  if (!image) return

  const scroller = gallery.querySelector('[ref="scroller"]')
  if (!scroller) return

  let slide = scroller.querySelector(`slideshow-slide[slide-id="${CSS.escape(slideId)}"]`)
  if (slide) {
    const img = slide.querySelector(".product-media__image")
    if (img instanceof HTMLImageElement && img.src !== image) img.src = image
  } else {
    slide = buildSlide(slideId, image)
    scroller.appendChild(slide)
  }

  const thumbnailsContainer = gallery.querySelector(".slideshow-controls__thumbnails")
  if (thumbnailsContainer) {
    let thumbnail = thumbnailsContainer.querySelector(`[data-product-mix-slide="${CSS.escape(slideId)}"]`)
    if (thumbnail) {
      const img = thumbnail.querySelector("img")
      if (img instanceof HTMLImageElement && img.src !== image) img.src = image
    } else {
      thumbnail = buildThumbnail(slideId, image)
      thumbnail.addEventListener("click", (event) => {
        event.preventDefault()
        gallery.slideshow?.select({ id: slideId })
      })
      thumbnailsContainer.appendChild(thumbnail)
    }
  }
}

/**
 * Every rendered copy of the product's base image, plus the slide holding it. The base image is the
 * gallery's first slide: on combo products the featured image (grid/listing shot) is deliberately
 * not rendered at all (see _product-media-gallery.liquid), so slide 1 is the plain base shot that
 * the combo images are variations of. Native slides carry no slide-id, hence the positional lookup.
 * @param {Element} gallery
 * @returns {{ slide: Element | null, images: HTMLImageElement[] }}
 */
function getBaseImages(gallery) {
  const slide = gallery.querySelector('[ref="scroller"] slideshow-slide:not([data-product-mix-slide])')
  const thumbnail = gallery.querySelector(
    ".slideshow-controls__thumbnails .slideshow-controls__thumbnail:not([data-product-mix-slide])"
  )
  const candidates = [
    slide?.querySelector(".product-media__image"),
    thumbnail?.querySelector("img"),
    gallery.querySelector(".dialog-zoomed-gallery > li:first-child .product-media__image"),
    gallery.querySelector(".dialog-thumbnails-list > button:first-child img"),
  ]

  return { slide, images: candidates.filter((img) => img instanceof HTMLImageElement) }
}

/**
 * Points a rendered base image at the combo image, remembering the original sources so the swap can
 * be undone once no combo applies again.
 * @param {HTMLImageElement} img
 * @param {string} image
 */
function swapBaseImage(img, image) {
  if (!image) return

  if (!("productMixBaseSrc" in img.dataset)) {
    img.dataset.productMixBaseSrc = img.getAttribute("src") || ""
    img.dataset.productMixBaseSrcset = img.getAttribute("srcset") || ""
    img.dataset.productMixBaseMaxRes = img.dataset.maxResolution || ""
  }

  if (img.getAttribute("src") !== image) img.setAttribute("src", image)
  // The responsive srcset would keep winning over the swapped src, so it has to go for as long as
  // the combo image is on display; it comes back verbatim on restore.
  img.removeAttribute("srcset")
  if (img.dataset.productMixBaseMaxRes) img.dataset.maxResolution = image
}

/**
 * @param {HTMLImageElement} img
 */
function restoreBaseImage(img) {
  if (!("productMixBaseSrc" in img.dataset)) return

  const { productMixBaseSrc, productMixBaseSrcset, productMixBaseMaxRes } = img.dataset
  if (productMixBaseSrc) img.setAttribute("src", productMixBaseSrc)
  if (productMixBaseSrcset) img.setAttribute("srcset", productMixBaseSrcset)
  if (productMixBaseMaxRes) img.dataset.maxResolution = productMixBaseMaxRes

  delete img.dataset.productMixBaseSrc
  delete img.dataset.productMixBaseSrcset
  delete img.dataset.productMixBaseMaxRes
}

/**
 * Removes any previously-injected combo slide/thumbnail not in `keepSlideIds`.
 * @param {Element} gallery
 * @param {Set<string>} keepSlideIds
 */
function pruneStaleSlides(gallery, keepSlideIds) {
  gallery.querySelectorAll("slideshow-slide[data-product-mix-slide]").forEach((slide) => {
    const slideId = slide.getAttribute("slide-id")
    if (slideId && !keepSlideIds.has(slideId)) slide.remove()
  })
  gallery.querySelectorAll("[data-product-mix-slide]").forEach((thumbnail) => {
    if (thumbnail.tagName === "SLIDESHOW-SLIDE") return
    const slideId = thumbnail.getAttribute("data-product-mix-slide")
    if (slideId && !keepSlideIds.has(slideId)) thumbnail.remove()
  })
}

/**
 * Shows/hides + populates the small "material-selection-display" overlay per product-mix
 * category — same element/behaviour the fabric/material picker already uses (see
 * variant-fabric-picker.js's updateMaterialDisplayFromInput/hideMaterialDisplay), keyed here by
 * `product_mix.<category>` (rendered in _product-media-gallery.liquid). Always the selected
 * component's own featured image, unconditionally — has nothing to do with combo matching.
 * @param {Array<{ category: string, select: HTMLSelectElement }>} categorySelects
 */
function updateMaterialDisplays(categorySelects) {
  for (const { category, select } of categorySelects) {
    const target = document.querySelector(`[data-material-display-key="product_mix.${CSS.escape(category)}"]`)
    if (!target) continue

    const selected = getSelectedOption(select)
    const thumb = target.querySelector(".material-selection-display__thumb")
    const text = target.querySelector(".material-selection-display__text")

    if (selected?.image) {
      if (thumb) thumb.style.backgroundImage = `url(${selected.image})`
      if (text) text.textContent = select.options[select.selectedIndex]?.dataset.upgradeVariantName || ""
      target.hidden = false
    } else {
      if (thumb) thumb.style.backgroundImage = ""
      if (text) text.textContent = ""
      target.hidden = true
    }
  }
}

/**
 * Resolves the current product-mix state for a scope and applies it: thumbnails always (from
 * each category's own featured image), main gallery only from matching combo-image entries.
 * @param {HTMLElement} scope - .product__options-combined
 * @param {{ jumpToCategory?: string }} [options]
 */
async function resolveAndApply(scope, options = {}) {
  const categorySelects = getCategorySelects(scope)
  if (!categorySelects.length) return

  updateMaterialDisplays(categorySelects)

  const comboEntries = getComboEntries(scope)
  const selections = categorySelects
    .map(({ category, select }) => ({ category, selected: getSelectedOption(select) }))
    .filter((entry) => entry.selected)
    .map((entry) => ({ category: entry.category, productId: entry.selected.productId }))

  const targets = resolveComboTargets(selections, comboEntries)

  const section = scope.closest(".shopify-section")
  const gallery = section?.querySelector("media-gallery")
  if (!gallery) return

  // The first target takes over the base image in place; only additional simultaneous matches are
  // appended as extra slides. Without a base slide to swap (product with no other media), every
  // target falls back to being appended.
  const base = getBaseImages(gallery)
  const primary = base.slide ? targets[0] : undefined
  const appended = primary ? targets.slice(1) : targets

  const keepSlideIds = new Set(appended.map((t) => t.slideId))
  pruneStaleSlides(gallery, keepSlideIds)

  for (const img of base.images) {
    if (primary?.image) swapBaseImage(img, primary.image)
    else restoreBaseImage(img)
  }

  if (!targets.length) {
    scope.removeAttribute("data-configured-image")
    return
  }

  // Guard against a load-order race: if media-gallery.js hasn't upgraded this element yet,
  // gallery.slideshow is undefined and the slide gets inserted but never actually shown.
  await customElements.whenDefined("media-gallery")

  for (const target of appended) {
    ensureSlide(gallery, target.slideId, target.image)
  }

  await nextFrame()

  // Prefer jumping to whichever target the customer's change just produced; on initial load (or
  // if the changed category isn't part of any match), jump to the first resolved target.
  const jumpTarget = (options.jumpToCategory && targets.find((t) => t.categories.includes(options.jumpToCategory))) || targets[0]
  if (jumpTarget === primary) {
    gallery.slideshow?.select(0, undefined, { animate: false })
  } else {
    gallery.slideshow?.select({ id: jumpTarget.slideId }, undefined, { animate: false })
  }

  scope.dataset.configuredImage = jumpTarget.image
}

function initScope(scope) {
  if (scope.dataset.productMixGalleryBound === "1") return
  scope.dataset.productMixGalleryBound = "1"

  resolveAndApply(scope)

  const section = scope.closest(".shopify-section")
  section?.addEventListener(ThemeEvents.variantUpdate, () => resolveAndApply(scope))
}

function initAll() {
  document.querySelectorAll(".product__options-combined").forEach((scope) => {
    if (scope.querySelector("select[data-mix-cap]")) initScope(scope)
  })
}

document.addEventListener("change", (event) => {
  const target = event.target
  if (!(target instanceof HTMLSelectElement) || !target.matches("select[data-mix-cap]")) return

  const scope = target.closest(".product__options-combined")
  if (!scope) return

  const category = (target.dataset.key || "").replace(/^product_mix\./, "")
  resolveAndApply(scope, { jumpToCategory: category })
})

document.addEventListener("shopify:section:load", initAll)

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll)
} else {
  initAll()
}
